import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Check, Pencil, X } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';

import { Button } from '@/components/ui/button';
import { EmptyArray, getStudentsWithGLARecords } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { formatInstant, isAirAdmin } from '@airreading/common';

import { useAllSchools, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useErrorToast } from '@/lib/error-handling';
import { useSorting } from '@/lib/sorting';
import { getStudentYearOptions } from '@/lib/student-utils';
import { airColumnHelper } from '@/lib/tables';

import { useTimeZone } from '@/auth/useTimeZone';
import {
  GetStudentsWithGLARecordsSortableColumn,
  getStudentsWithGLARecordsSortableColumnSchema,
  getStudentsWithGLARecordsSortOrderSchema,
  GlaFollowUpStatus,
  StudentWithGLARecord,
  StudentYearColorMap,
  studentYearOption,
  studentYearSchema,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { EditStudentGLA } from '../-components/edit-student-gla';
import { studentsTabs } from '../-components/tabs';

const glaSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schoolIds: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  sortBy: getStudentsWithGLARecordsSortOrderSchema.optional().catch(['onDemandGLACount-desc']),
});

export const Route = createFileRoute('/_layout/students/gla/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: GLAStudentsIndex,
  validateSearch: glaSearchSchema,
});

const col = airColumnHelper<StudentWithGLARecord>(getStudentsWithGLARecordsSortableColumnSchema);

const followUpStatusLabels: Record<string, string> = {
  [GlaFollowUpStatus.need_follow_up]: 'Need Follow Up',
  [GlaFollowUpStatus.check_again]: 'Check Again',
  [GlaFollowUpStatus.resolved]: 'Resolved',
};

const buildColumns = (timeZone: string, schoolIdToDistrictName: Record<string, string>) => {
  const now = Temporal.Now.instant();

  return [
    col.accessor((row) => row, {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ getValue }) => (
        <EditStudentGLA
          student={getValue()}
          trigger={
            <Button variant="ghost" size="sm" title="GLA follow-up">
              <Pencil className="h-4 w-4" />
            </Button>
          }
        />
      ),
    }),
    col.accessor((row) => row.id, {
      id: 'fullNameOrEmail',
      header: 'Student name',
      cell: ({ row, getValue }) => {
        const { firstName, lastName, email } = row.original;
        const name = [firstName, lastName].filter(Boolean).join(' ').trim() || email;
        return (
          <Link
            to={'/students/$studentId'}
            params={{ studentId: getValue() }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
            className="text-blue-600 hover:text-blue-800"
          >
            {name}
          </Link>
        );
      },
    }),
    col.accessor('userRoles', {
      id: 'schoolName',
      header: 'School - District',
      cell: ({ getValue }) => {
        const roles = getValue();
        const studentRole = roles.find((r) => r.school);
        const school = studentRole?.school;
        if (!school) return <span>—</span>;
        const districtName = schoolIdToDistrictName[school.id];
        return (
          <Link
            to="/orgs/schools/$schoolId/schedules"
            className="text-blue-600 hover:text-blue-800"
            params={{ schoolId: school.id }}
          >
            {districtName ? `${school.name} - ${districtName}` : school.name}
          </Link>
        );
      },
    }),
    col.accessor('grade', {
      id: 'grade',
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue();
        if (!grade) return studentYearOption(grade);
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),
    col.accessor('glaFollowUpStatus', {
      id: 'glaFollowUpStatus',
      header: 'Follow-up status',
      enableSorting: false,
      cell: ({ getValue }) => {
        const status = getValue();
        if (!status) return <span>—</span>;
        return <span>{followUpStatusLabels[status] ?? status}</span>;
      },
    }),
    col.accessor('onDemandGLAs', {
      id: 'missedGlaDates',
      header: 'Missed GLA dates',
      enableSorting: false,
      cell: ({ getValue }) => {
        const glas = getValue().filter(
          (g) =>
            Temporal.Instant.compare(g.sourceSessionStartInstant, now) < 0 &&
            (!g.sessionFeedback.studentAttended || !g.sessionFeedback.glaAssessmentCompleted),
        );
        if (glas.length === 0) return <span>—</span>;
        return (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {glas.map((g) => (
              <span key={g.id} className="text-xs flex items-center gap-1">
                <Link
                  to="/sessions/session-feedbacks/$feedbackId"
                  params={{
                    feedbackId: toSessionFeedbackCompoundId(g.sessionFeedback),
                  }}
                  className="text-blue-600 hover:text-blue-800"
                  search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
                >
                  {formatInstant(g.sourceSessionStartInstant, timeZone).formattedDate}
                </Link>
                {g.sessionFeedback.studentAttended ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <X className="h-3 w-3 text-red-500" />
                )}
              </span>
            ))}
          </div>
        );
      },
    }),
    col.accessor('onDemandGLAs', {
      id: 'onDemandGLACount',
      header: 'Number of missed GLA dates',
      cell: ({ getValue }) => {
        const count = getValue().filter(
          (g) =>
            Temporal.Instant.compare(g.sourceSessionStartInstant, now) < 0 &&
            (!g.sessionFeedback.studentAttended || !g.sessionFeedback.glaAssessmentCompleted),
        ).length;
        return <span>{count}</span>;
      },
    }),
    col.accessor((row) => row, {
      id: 'futureGlaDates',
      header: 'Upcoming GLA dates',
      enableSorting: false,
      cell: ({ getValue }) => {
        const student = getValue();
        const upcomingOnDemandGLAs = student.onDemandGLAs.filter(
          (g) => Temporal.Instant.compare(g.onDemandGLASessionStartInstant, now) >= 0,
        );
        const onDemandSessionIds = new Set(upcomingOnDemandGLAs.map((g) => g.onDemandGLASessionId));
        const upcomingRegularGLAs = student.upcomingRegularGLASessions.filter(
          (s) => !onDemandSessionIds.has(s.sessionId),
        );

        const allDates: { sessionId: string; startInstant: Temporal.Instant }[] = [
          ...upcomingOnDemandGLAs.map((g) => ({
            sessionId: g.onDemandGLASessionId,
            startInstant: g.onDemandGLASessionStartInstant,
          })),
          ...upcomingRegularGLAs.map((s) => ({ sessionId: s.sessionId, startInstant: s.startInstant })),
        ].sort((a, b) => Temporal.Instant.compare(a.startInstant, b.startInstant));

        if (allDates.length === 0) return <span>—</span>;
        return (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {allDates.map((d) => (
              <Link
                key={d.sessionId}
                to="/sessions/$sessionId"
                params={{ sessionId: d.sessionId }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {formatInstant(d.startInstant, timeZone).formattedDate}
              </Link>
            ))}
          </div>
        );
      },
    }),
    col.accessor('onDemandGLAs', {
      id: 'completedGlaDates',
      header: 'Completed GLA dates',
      enableSorting: false,
      cell: ({ getValue }) => {
        const glas = getValue().filter(
          (g) =>
            Temporal.Instant.compare(g.sourceSessionStartInstant, now) < 0 &&
            g.sessionFeedback.studentAttended &&
            g.sessionFeedback.glaAssessmentCompleted,
        );
        if (glas.length === 0) return <span>—</span>;
        return (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {glas.map((g) => (
              <Link
                key={g.id}
                to="/sessions/session-feedbacks/$feedbackId"
                params={{
                  feedbackId: toSessionFeedbackCompoundId(g.sessionFeedback),
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
                search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
              >
                {formatInstant(g.sourceSessionStartInstant, timeZone).formattedDate}
              </Link>
            ))}
          </div>
        );
      },
    }),
    col.accessor('glaReviewer', {
      id: 'glaReviewer',
      header: 'Reviewer',
      enableSorting: false,
      cell: ({ getValue }) => {
        const reviewer = getValue();
        if (!reviewer) return <span>—</span>;
        return <span>{reviewer.fullNameOrEmail}</span>;
      },
    }),
    col.accessor('glaReviewNotes', {
      id: 'glaReviewNotes',
      header: 'Review notes',
      enableSorting: false,
      cell: ({ getValue }) => {
        const notes = getValue();
        if (!notes) return <span>—</span>;
        return <span className="text-xs whitespace-pre-wrap">{notes}</span>;
      },
    }),
  ];
};

function GLAStudentsIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();

  const schoolIdToDistrictName = Object.fromEntries(allSchools.map((school) => [school.id, school.org.name]));
  const columns = useMemo(() => buildColumns(timeZone, schoolIdToDistrictName), [timeZone, schoolIdToDistrictName]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery], setUserNameQuery],
      schoolIds: [[selectedSchoolIds, activeSelectedSchoolIds], setSelectedSchoolIds],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schoolIds', 'grades']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsWithGLARecordsSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const allSchoolOptions = useSchoolAndOrgOptions({});
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudentsWithGLARecords({
      pageIndex,
      pageSize,
      filters: {
        query: userNameQuery,
        schoolIds: activeSelectedSchoolIds,
        grades: activeSelectedGrades,
      },
      sortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading GLA records. Please try again.');

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
    },
    rowCount: response?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    enableColumnResizing: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchoolIds ?? []}
          onChange={setSelectedSchoolIds}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchoolIds,
      setSelectedSchoolIds,
      selectedGrades,
      setSelectedGrades,
      resetFilters,
    ],
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('GLA')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students with GLA records found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}
